import React, { useEffect, useState, useMemo, useContext } from "react";
import { TextField, Button } from "@mui/material";

import DataTable from "react-data-table-component";
import moment from "moment";

import ExpandedAudit from "../../components/ExpandedAudit";

import Swal from "sweetalert2";

import useRejectedAudits from "../../queries/audit/getRejectedAudits";
import useProperties from "../../queries/property/getProperties";
import useCompanies from "../../queries/gift/companies";
import useRoomTypes from "../../queries/roomType/getRoomTypeLinks";
import useGifts from "../../queries/gift/getGifts";
import useCities from "../../queries/city/getCities";
import useRateTypes from "../../queries/roomType/getRateTypes";
import parseRequirements from "../../utils/parseRequirements";
import Loader from "../../components/Loader";
import { AuthContext } from "../../utils/userProvider";
import {
  ListPageShell,
  ListPageCard,
  ListPageHeader,
  dataTableCustomStyles,
} from "../../components/ListPage";

const PendingAudits = () => {
  const [transactionTable, setTransactionTable] = useState([]);
  const { authState, setAuthState } = useContext(AuthContext);

  function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  function viewDescription(title, data) {
    Swal.fire({
      title: title,
      html: `<p style='white-space: pre-line; text-align: left'>${data}</p>`,
    });
  }

  let columns = [
    {
      name: "Executive",
      selector: "executive",
      sortable: true,
      cell: (row, index, column, id) => {
        if (row.executive) {
          return <span>{row.executive.email}</span>;
        } else {
          return <span>{"<DELETED>"}</span>;
        }
      },
    },
    {
      name: "Handler",
      selector: "handler",
      sortable: true,
      cell: (row, index, column, id) => {
        if (row.handler) {
          return <span>{row.handler.email}</span>;
        } else {
          return <span>{"<DELETED>"}</span>;
        }
      },
    },
    {
      name: "Type",
      selector: "type",
      sortable: true,
      cell: (row, index, column, id) => {
        return <span>{capitalizeFirstLetter(row.type)}</span>;
      },
    },
    {
      name: "Action",
      selector: "action",
      sortable: true,
      cell: (row, index, column, id) => {
        return (
          <span>
            {row.action == "createManual"
              ? "Create Manual"
              : capitalizeFirstLetter(row.action)}
          </span>
        );
      },
    },
    {
      name: "Action Description",
      selector: "actionDescription",
      sortable: true,
      cell: (row, index, column, id) => {
        return (
          <div>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                viewDescription("Action Description", row.actionDescription);
              }}
            >
              Action Desc
            </Button>
          </div>
        );
      },
    },
    {
      name: "Resolution Description",
      selector: "resolutionDescription",
      sortable: true,
      cell: (row, index, column, id) => {
        return (
          <div>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => {
                viewDescription(
                  "Resolution Description",
                  row.resolutionDescription
                );
              }}
            >
              Resolution Desc
            </Button>
          </div>
        );
      },
    },
    {
      name: "Created At",
      selector: "createdAt",
      sortable: true,
      cell: (row, index, column, id) => {
        return (
          <span>{moment(row.createdAt).format("DD MMM YYYY hh:mmA")}</span>
        );
      },
    },
    {
      name: "Updated At",
      selector: "updatedAt",
      sortable: true,
      cell: (row, index, column, id) => {
        return (
          <span>{moment(row.updatedAt).format("DD MMM YYYY hh:mmA")}</span>
        );
      },
    },
  ];

  const [response, reset] = useRejectedAudits();
  const [properties, resetProperties] = useProperties();
  const [companies, resetCompanies] = useCompanies();
  const [roomTypes, resetRooms] = useRoomTypes();
  const [gifts, resetGifts] = useGifts();
  const [rateTypes, resetRateTypes] = useRateTypes();
  const [cities, resetCities] = useCities();

  const [filterText, setFilterText] = useState("");

  const handleFilterText = (event) => {
    setFilterText(event.target.value);
  };

  const filteredItems = transactionTable.filter((item) => {
    if (
      filterText != "" &&
      (authState.role != "Executive" || item.executive?.id == authState.userId)
    ) {
      // Check for reservation ID in credit-notes type audits
      const reservationIdMatch = item.type === "credit-notes" && 
        item.snapshotCurrent?.transaction?.reservationId &&
        item.snapshotCurrent.transaction.reservationId.toString()
          .toLowerCase()
          .includes(filterText.toLowerCase());

      return (
        item.type?.toLowerCase().includes(filterText.toLowerCase()) ||
        item.executive?.email
          .toLowerCase()
          .includes(filterText.toLowerCase()) ||
        item.handler?.email.toLowerCase().includes(filterText.toLowerCase()) ||
        item.action?.toLowerCase().includes(filterText.toLowerCase()) ||
        moment(item.createdAt)
          .format("DD MMM YYYY hh:mmA")
          .includes(filterText.toLowerCase()) ||
        moment(item.updatedAt)
          .format("DD MMM YYYY hh:mmA")
          .includes(filterText.toLowerCase()) ||
        reservationIdMatch
      );
    } else {
      return (
        authState.role != "Executive" || item.executive?.id == authState.userId
      );
    }
  });

  useEffect(() => {
    if (response.data) {
      setTransactionTable([...response.data]);
    }
  }, [response]);

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div>
        <TextField
          id="filter-text"
          onChange={handleFilterText}
          label="Search"
          variant="outlined"
          size="small"
        />
      </div>
    );
  }, [filterText]);

  return (
    <ListPageShell>
      <ListPageCard>
        {(response.loading ||
          properties.loading ||
          companies.loading ||
          roomTypes.loading ||
          gifts.loading ||
          cities.loading) && <Loader />}
        {response.data &&
          properties.data &&
          companies.data &&
          roomTypes.data &&
          gifts.data &&
          cities.data && (
            <div>
              <ListPageHeader title="Rejected Audit Trails" />
              <DataTable
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 30]}
                noHeader={true}
                subHeader
                data={filteredItems}
                columns={columns}
                expandableRows
                highlightOnHover
                expandOnRowClicked={true}
                expandableRowsComponent={
                  <ExpandedAudit
                    properties={properties.data}
                    companies={parseRequirements(companies.data)}
                    room_types={roomTypes.data}
                    gifts={gifts.data}
                    cities={cities.data}
                    rateTypes={rateTypes.data}
                  />
                }
                subHeaderComponent={subHeaderComponentMemo}
                customStyles={dataTableCustomStyles}
              />
            </div>
          )}
      </ListPageCard>
    </ListPageShell>
  );
};

export default PendingAudits;
